import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { MatchingSession } from '@data/session/session.model';
import { NotificationService } from '@core/services/notification/notification.service';
import { SyncService } from '@data/sync/sync.service';
import { StopSyncResult } from '@data/sync/sync.model';

/**
 * Organizer dialog for the one-way "Sync to RECREWT". Reads the source (anymatch)
 * session from `MAT_DIALOG_DATA`, shows the RECREWT session it syncs to (asks for one
 * while none is stored) and what the next sync would do, triggers the sync, and then
 * renders the per-Stop result plus the last-synced lineage.
 * The copy makes the one-way / no-auto-sync contract explicit.
 */
@Component({
  selector: 'recrewt-sync-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    TranslateModule,
  ],
  templateUrl: './sync-dialog.component.html',
  styleUrls: ['./sync-dialog.component.scss'],
})
export class SyncDialogComponent {
  readonly session = inject<MatchingSession>(MAT_DIALOG_DATA);

  private sync = inject(SyncService);
  private notify = inject(NotificationService);
  private dialogRef = inject<MatDialogRef<SyncDialogComponent>>(MatDialogRef);

  readonly targetControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  /** `undefined` while loading, `null` if it failed (the sync works without it). */
  readonly status = toSignal(this.sync.status(this.session.id).pipe(catchError(() => of(null))));

  /** The RECREWT session stored on the session; the typed one is only used while there is none. */
  readonly storedTarget = computed(
    () => this.status()?.target ?? this.session.recrewtSessionRef ?? null,
  );

  readonly syncing = signal(false);
  readonly results = signal<StopSyncResult[] | null>(null);
  readonly targetSessionId = signal<string | null>(null);
  readonly syncedAt = signal<Date | null>(null);

  readonly summary = computed(() => {
    const r = this.results();
    if (!r) return null;
    const count = (s: string): number => r.filter((x) => x.status === s).length;
    return {
      total: r.length,
      created: count('created'),
      updated: count('updated'),
      unchanged: count('unchanged'),
      exited: count('exited'),
      skipped: count('skipped_no_name') + count('skipped_not_recruiting'),
      failed: count('error'),
    };
  });

  submit(): void {
    if (this.syncing()) return;
    const typed = this.storedTarget() ? undefined : this.targetControl.value.trim();
    if (typed === '') {
      this.targetControl.markAsTouched();
      return;
    }

    this.syncing.set(true);
    this.sync.syncSessionToRecrewt(this.session.id, typed).subscribe({
      next: ({ target, results }) => {
        this.syncing.set(false);
        this.results.set(results);
        this.targetSessionId.set(target);
        this.syncedAt.set(new Date());
        const failed = results.filter((r) => r.status === 'error').length;
        if (failed > 0) {
          this.notify.error('SYNC.partialError');
        } else {
          this.notify.success('SYNC.success');
        }
      },
      error: () => {
        this.syncing.set(false);
        this.notify.error('SYNC.error');
      },
    });
  }

  icon(status: string): string {
    switch (status) {
      case 'created':
      case 'updated':
      case 'ok':
        return 'check_circle';
      case 'unchanged':
      case 'not_synced':
        return 'remove_circle_outline';
      case 'exited':
        return 'logout';
      case 'skipped_no_name':
        return 'report_problem';
      case 'skipped_not_recruiting':
        return 'work_off';
      case 'error':
        return 'error';
      default:
        return 'help_outline';
    }
  }

  statusKey(status: string): string {
    const known = [
      'created',
      'updated',
      'unchanged',
      'skipped_no_name',
      'skipped_not_recruiting',
      'exited',
      'not_synced',
      'ok',
      'error',
    ];
    return known.includes(status) ? `SYNC.RESULT.${status}` : 'SYNC.RESULT.unknown';
  }

  close(): void {
    this.dialogRef.close();
  }
}
